import { travelerForm } from '@eti/travelers';
import type { Country, GetSelectedTripOnTravelerDetailsPageQuery } from '@eti/schema-types';
import type {
  CountryType,
  SelfServiceRebookingTravelers,
  TransformedTravelDocument,
  TransformedTravelersDataType,
} from '../../../common/utils/models';
import { getPersistedFormData } from './persistedData';

const {
  DATE_OF_BIRTH,
  FIRST_NAME,
  FREQUENT_FLYER,
  FREQUENT_FLYER_CARD_NUMBER,
  FREQUENT_FLYER_CARD_TYPE,
  GENDER,
  LAST_NAME,
  SELF_SERVICE_REBOOKING_GENDER,
  SELF_SERVICE_REBOOKING_FIRST_NAME,
  SELF_SERVICE_REBOOKING_LAST_NAME,
  SELF_SERVICE_REBOOKING_DATE_OF_BIRTH,
  TRAVEL_DOCUMENT_EXPIRY_DATE,
  TRAVEL_DOCUMENT_ISSUING_COUNTRY,
  TRAVEL_DOCUMENT_NUMBER,
  TRAVEL_DOCUMENT_TYPE,
} = travelerForm;

const getSelfServiceRebookingData = (
  property: keyof NonNullable<NonNullable<SelfServiceRebookingTravelers>[number]>,
  selfServiceRebookingTravelers?: SelfServiceRebookingTravelers,
  id?: string | null,
) => {
  if (selfServiceRebookingTravelers && typeof id === 'string') {
    const self = selfServiceRebookingTravelers?.find((traveler) => {
      if (traveler) {
        return traveler.id === id;
      }
      return false;
    });
    if (self && self[property]) {
      return self[property];
    }
  }

  return undefined;
};

const getFrequentFlyerPersistedData = (
  id?: string | null,
  shouldShowFrequentFlyerCard?: boolean | null,
) => {
  if (!shouldShowFrequentFlyerCard) {
    return {};
  }
  const storedCardType = `traveler-${id}-${FREQUENT_FLYER}-${FREQUENT_FLYER_CARD_TYPE}`;
  const storedCardNumber = `traveler-${id}-${FREQUENT_FLYER}-${FREQUENT_FLYER_CARD_NUMBER}`;

  return {
    [FREQUENT_FLYER]: {
      [FREQUENT_FLYER_CARD_TYPE]: getPersistedFormData(storedCardType),
      [FREQUENT_FLYER_CARD_NUMBER]: getPersistedFormData(storedCardNumber),
    },
  };
};

const getTravelDocumentData =
  (id?: string | null) => (fieldName: string, isVisible?: boolean | null) => {
    if (!isVisible) {
      return {};
    }
    const fieldKey = `${fieldName}-${id}`;
    return {
      [fieldKey]: getPersistedFormData(fieldKey),
    };
  };

const getFormattedIssuingCountry = (country?: CountryType | Omit<Country, 'states'> | undefined) =>
  country
    ? {
        code: country.code,
        label: country.name,
        value: String(country.id),
      }
    : undefined;

const getInitialTravelDocumentType = (id?: string | null) => {
  const persistedTravelDocumentType = getPersistedFormData(
    `traveler-${id}-${TRAVEL_DOCUMENT_TYPE}`,
  );
  return persistedTravelDocumentType ? { [TRAVEL_DOCUMENT_TYPE]: persistedTravelDocumentType } : {};
};

const getTravelDocumentPersistedData = (
  id?: string | null,
  travelDocument: TransformedTravelDocument | null = {},
) => {
  const travelerDocument = getTravelDocumentData(id);

  return {
    ...travelerDocument(
      TRAVEL_DOCUMENT_NUMBER,
      travelDocument?.shouldShowTravelDocumentPassportNumber,
    ),
    ...travelerDocument(
      TRAVEL_DOCUMENT_EXPIRY_DATE,
      Boolean(travelDocument?.requireTravelDocumentExpiryDate),
    ),
    ...getInitialTravelDocumentType(id),
  };
};

const getLocalizedDateOfBirth = (birthDate: string, dateFormat: string = 'yyyy-MM-dd') => {
  const parts = birthDate.split('-');
  const map = ['yyyy', 'MM', 'dd'];
  const datePattern = dateFormat.split('-');

  const firstItem = map.indexOf(datePattern[0]);
  const secondItem = map.indexOf(datePattern[1]);
  const thirdItem = map.indexOf(datePattern[2]);

  return [parts[firstItem], parts[secondItem], parts[thirdItem]].join('-');
};

export const getInitialValuesTravelerForm = (
  travelers: TransformedTravelersDataType = [],
  market: CountryType,
  countries: GetSelectedTripOnTravelerDetailsPageQuery['countries'],
  formDateInputFormat: string,
  shouldUsePersistedFormData: boolean = true,
  selfServiceRebookingTravelers?: SelfServiceRebookingTravelers,
  isSelfServiceRebooking?: boolean,
) =>
  travelers.reduce(
    (
      acc,
      {
        birthDate,
        firstName,
        gender,
        id,
        lastName,
        nationality,
        travelDocument,
        showDateOfBirth,
        shouldShowFrequentFlyerCard,
      },
    ) => {
      const showNationality = travelDocument?.shouldShowTravelDocumentNationality;
      const formSectionName = travelerForm.getTravelerFormSectionName(id);
      const persistedGender = shouldUsePersistedFormData
        ? getPersistedFormData(`${GENDER}-${id}`)
        : undefined;
      const persistedFirstName = shouldUsePersistedFormData
        ? getPersistedFormData(`${FIRST_NAME}-${id}`)
        : undefined;
      const persistedLastName = shouldUsePersistedFormData
        ? getPersistedFormData(`${LAST_NAME}-${id}`)
        : undefined;
      const persistedDateOfBirth = shouldUsePersistedFormData
        ? getPersistedFormData(`${DATE_OF_BIRTH}-${id}`)
        : undefined;
      const persistedNationality = shouldUsePersistedFormData
        ? getPersistedFormData(`traveler-${id}-${TRAVEL_DOCUMENT_ISSUING_COUNTRY}`)
        : undefined;

      const initialGender = isSelfServiceRebooking
        ? getSelfServiceRebookingData(
            `${SELF_SERVICE_REBOOKING_GENDER}`,
            selfServiceRebookingTravelers,
            id,
          )
        : persistedGender || gender;

      const initialFirstName = isSelfServiceRebooking
        ? getSelfServiceRebookingData(
            `${SELF_SERVICE_REBOOKING_FIRST_NAME}`,
            selfServiceRebookingTravelers,
            id,
          )
        : persistedFirstName || firstName;

      const initialLastName = isSelfServiceRebooking
        ? getSelfServiceRebookingData(
            `${SELF_SERVICE_REBOOKING_LAST_NAME}`,
            selfServiceRebookingTravelers,
            id,
          )
        : persistedLastName || lastName;

      const localizedDateOfBirth = birthDate
        ? getLocalizedDateOfBirth(birthDate, formDateInputFormat)
        : undefined;
      const initialDateOfBirth = isSelfServiceRebooking
        ? getSelfServiceRebookingData(
            `${SELF_SERVICE_REBOOKING_DATE_OF_BIRTH}`,
            selfServiceRebookingTravelers,
            id,
          )
        : persistedDateOfBirth || localizedDateOfBirth;

      const defaultNationality = countries.find((c) => c.code === nationality) ?? market;
      const initialTravelDocumentIssuingCountry =
        persistedNationality ?? getFormattedIssuingCountry(defaultNationality);

      return {
        ...acc,
        [formSectionName]: {
          [`${GENDER}-${id}`]: initialGender,
          [`${FIRST_NAME}-${id}`]: initialFirstName,
          [`${LAST_NAME}-${id}`]: initialLastName,
          ...(showDateOfBirth && {
            [`${DATE_OF_BIRTH}-${id}`]: initialDateOfBirth,
          }),
          ...(showNationality && {
            [TRAVEL_DOCUMENT_ISSUING_COUNTRY]: initialTravelDocumentIssuingCountry,
          }),
          ...(shouldUsePersistedFormData
            ? getFrequentFlyerPersistedData(id, shouldShowFrequentFlyerCard)
            : {}),
          ...(shouldUsePersistedFormData ? getTravelDocumentPersistedData(id, travelDocument) : {}),
        },
      };
    },
    {},
  );
